import { IconButton, ListItemText, Drawer, List, useMediaQuery, useTheme, ListItemButton, styled, Box } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { FromStore } from '../../../helpers/Store';
import { ApiGetUserProfile } from '../../../stores/api/user/actions';
import { StyledAppBar, StyledMagicBotLogo, StyledToolbar, TypographyEllipsisStyled } from '../../../style/GlobalsComponentsStyled';
import { Logout } from '../../../stores/auth/actions';
import ServiceLocator from '../../../services/ServiceLocator';
import { HandleError, HandleSuccess, ShowErrorMessage } from '../../../stores/page/actions';
import { Constants } from '../../../api/Configuration';
import Button from '../../../PublicSide/components/Button/Button';
import { GameResponse } from '../../../api/dtos/application/GameResponse';
import LogOutRequest from '../../../api/dtos/auth/LogOutRequest';
import { useAppDispatch } from '../../../helpers/UseAppDispatch';
import NavBar from '../../../PublicSide/components/NavBar/NavBar';

const StyledDrawer = styled(Drawer)({
    '& .MuiDrawer-paper': {
        backgroundColor: '#1C1D2B',
        color: '#fff',
        position: 'fixed',
        top: '65px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        height: 'calc(100vh - 65px)',
    }
})

const StyledList = styled(List)(({ theme }) => ({
    marginTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
        alignItems: 'flex-start',
        marginTop: '2rem',
    }
}))

const UserInfoBox = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    paddingBottom: '1rem',
    paddingTop: '1rem',
    position: 'relative',
    cursor: 'pointer',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '1px',
        background: 'linear-gradient(to top right, #CDC7E5 0%, #A92FFB 28%, #891CFB 59%, #2F27CE 100%)',
    }
})

const Navigation = () => {
    const dispatch = useAppDispatch();
    const [open, setOpen] = useState(false);
    const Navigate = useNavigate();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    const { user } = FromStore(state => state.apis.profile);

    useEffect(() => {
        dispatch(ApiGetUserProfile());
    }, [dispatch]);

    const handleLogout = async () => {
        const refreshToken = localStorage.getItem('auth:refreshToken');
        const clientId = Constants.CLIENT_ID;
        if (!refreshToken || !clientId) {
            dispatch(ShowErrorMessage('Impossible to log out.'));
            return;
        }
        const request: LogOutRequest = {
            refresh_token: refreshToken,
            client_id: clientId
        };
        const response = await ServiceLocator.AuthService.Logout(request);
        if (response.is_success) {
            ServiceLocator.Authenticator.Logout();
            dispatch(Logout());
            dispatch(HandleSuccess('Logout successfully.'));
            Navigate('/login');
        }
        else {
            dispatch(HandleError(response));
        }

    };
    const handleNavigation = (path: any, game?: GameResponse) => {
        window.scrollTo({ top: 0 });
        Navigate(path, { state: { game } });
        (!isDesktop) && setOpen(false);
    };

    return (
        <>
            <StyledAppBar>
                <StyledToolbar>
                    {!isDesktop ? (
                        <>
                            <IconButton edge="end" color="inherit" onClick={() => setOpen(!open)} aria-label='open navigation menu'>
                                <DashboardOutlinedIcon />
                            </IconButton>
                            <IconButton onClick={() => handleNavigation('/')} aria-label='go to home page' >
                                <StyledMagicBotLogo />
                            </IconButton>
                        </>
                    ) : (
                        <NavBar />
                    )}
                    {/* Menu */}
                    <StyledDrawer anchor={'left'} open={isDesktop || open} onClose={() => setOpen(false)} variant={isDesktop ? 'permanent' : 'temporary'}>
                        <StyledList>
                            <ListItemButton onClick={() => handleNavigation('/dashboard')}>
                                {isDesktop ? (
                                    <ListItemText primary='All servers' />
                                ) : (
                                    <ListItemText primary='All' />
                                )}
                            </ListItemButton>
                            <ListItemButton onClick={() => handleNavigation('/guns-of-glory', GameResponse.GunsOfGlory)}>
                                {isDesktop ? (
                                    <ListItemText primary='Guns of Glory' />
                                ) : (
                                    <ListItemText primary='G' />
                                )}
                            </ListItemButton>
                            <ListItemButton onClick={() => handleNavigation('/misty-continent', GameResponse.MistyContinent)}>
                                {isDesktop ? (
                                    <ListItemText primary='Misty Continent' />
                                ) : (
                                    <ListItemText primary='M' />
                                )}
                            </ListItemButton>
                            <ListItemButton onClick={() => handleNavigation('/stormshot', GameResponse.StormShot)}>
                                {isDesktop ? (
                                    <ListItemText primary='Stormshot' />
                                ) : (
                                    <ListItemText primary='S' />
                                )}
                            </ListItemButton>
                            {user.role === 'Admin' && (
                                <ListItemButton onClick={() => handleNavigation('/management')}>
                                    {isDesktop ? (
                                        <Box display='flex' gap='1rem'>
                                            <ManageAccountsOutlinedIcon />
                                            <ListItemText primary='Admins' />
                                        </Box>
                                    ) : (
                                        <ManageAccountsOutlinedIcon />
                                    )}
                                </ListItemButton>
                            )}
                        </StyledList>
                        <Box width='100%' paddingBottom='1rem' {...(!isDesktop && { display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" })}  >
                            <UserInfoBox onClick={() => handleNavigation('/profile')}>
                                <AccountCircleOutlinedIcon />
                                {isDesktop && user && (
                                    <Box>
                                        <TypographyEllipsisStyled>{user.name}</TypographyEllipsisStyled>
                                        <TypographyEllipsisStyled>{user.email}</TypographyEllipsisStyled>
                                    </Box>
                                )}
                            </UserInfoBox>
                            <Button text='Logout' onClick={handleLogout} style={{ width: '100%' }} />
                        </Box>
                    </StyledDrawer>
                </StyledToolbar>
            </StyledAppBar>
        </>
    );

};

export default Navigation;
